import {useEffect, useState} from 'react'
import {getVideoMimeType} from 'src/common/helpers'

type DeferredVideoProps = {
  videoUrl: string
  posterUrl?: string | null
  className?: string
}

/**
 * Renders a looping, muted, autoplaying video only after mount, so its bytes don't compete with
 * critical rendering-path resources (fonts, CSS, JS) during the initial paint / LCP window. Until
 * then, a lightweight poster image (when provided) is shown instead — this keeps a fast, cheap
 * paint target for LCP rather than an empty box. The poster is also passed to the `<video>` itself,
 * so it doubles as a static fallback if the browser fails to play the video (e.g. an unsupported
 * or misconfigured source).
 */
export const DeferredVideo = ({videoUrl, posterUrl, className}: DeferredVideoProps) => {
  const [shouldLoad, setShouldLoad] = useState(false)

  // Defer past the initial paint instead of loading the video eagerly during hydration
  useEffect(() => {
    setShouldLoad(true)
  }, [])

  if (!shouldLoad) {
    return posterUrl ? <img src={posterUrl} alt="" fetchPriority="high" className={className} /> : null
  }

  return (
    <video autoPlay muted loop playsInline poster={posterUrl ?? undefined} className={className}>
      <source src={videoUrl} type={getVideoMimeType(videoUrl)} />
    </video>
  )
}
